import {type PIXI} from '@colonist/pixi'
import {GameColor} from '../GameColor.ts'
import {GameFont} from '../GameFont.ts'
import {logError} from '../ui_utils/Utils.ts'
import {TextView} from './TextView.ts'
import {StringUtils} from '@colonist/utils'
import {type TranslationObject} from '@colonist/i18n'

export class TextTruncatedView extends TextView {
    private readonly numberOfDots: number = 2

    constructor(text: TranslationObject, x: number, y: number, w: number, h: number, container: PIXI.Container, fontSize: number = GameFont.Size.Normal, color: string = GameColor.getColor(GameColor.Color.Gray0), centerY: boolean = true) {
        super(text, x, y, w, h, container, fontSize, color, centerY, {wordWrap: false}, false)

        this.sliceTextToFitContainer()
        this.updateToolTipText(text)
    }

    centerXInParent() {
        super.centerXInParent()
        this.repositionTextForMask()
    }

    centerX() {
        super.centerX()
        this.repositionTextForMask()
    }

    private repositionTextForMask() {
        const isTextWiderThanMask = this.width > this.initialW
        if(!isTextWiderThanMask) return
        this.changeStyle({align: 'left'})
        this.x = this.initialX
    }

    setText(text: TranslationObject, fitText: boolean = true) {
        super.setText(text)

        this.updateToolTipText(text)
        if(fitText) this.sliceTextToFitContainer()
    }

    makeBold() {
        super.makeBold(false)
    }

    makeSemiBold() {
        super.makeBold(false)
    }

    removeBold() {
        super.removeBold(false)
    }

    private sliceTextToFitContainer() {
        if(this.width < this.initialW) return
        const text = this.getText()
        if(text.length <= this.numberOfDots + 1) return logError('TextViewMasked', ['container is too small to fit text'])

        if(!this.areLastCharactersDots(text)) {
            const dotText = this.getReplacementDotsForText()
            const textWithTrailingDots = text + dotText
            this.setText({key: 'strings:colonistCommon.utils.#buffer', options: {
                value: textWithTrailingDots,
                interpolation: {escapeValue: false},
            }}, false)
        }

        const currentText = this.getText()
        const characterIndexToRemove = currentText.length - this.numberOfDots - 1
        const textWithoutLastNonDotChar = StringUtils.replaceStringAt(currentText, characterIndexToRemove, '')
        this.setText({key: 'strings:colonistCommon.utils.#buffer', options: {
            value: textWithoutLastNonDotChar,
            interpolation: {escapeValue: false},
        }})
    }

    private updateToolTipText(text: TranslationObject) {
        const isContentTooLargeForContainer = this.width >= this.initialW
        if(isContentTooLargeForContainer) {
            this.setTip(text, [{key: 'strings:colonistCommon.utils.emptyString'}])
            return
        }
        this.disableTip()
    }

    private getReplacementDotsForText(): string {
        const dotText = '.'.repeat(this.numberOfDots)
        return dotText
    }

    private areLastCharactersDots(text: string): boolean {
        const lastCharacters = text.slice(-this.numberOfDots)
        const dotText = this.getReplacementDotsForText()
        return lastCharacters == dotText
    }
}
